/** Tech Library — one-click review queue for externally imported
 *  documents (Icecat etc.): bulk mark CUSTOMER SAFE / internal / restricted. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Ban, ChevronDown, ChevronUp } from "lucide-react";

const TYPE_LABEL = (t) => (t || "").replace(/_/g, " ");

export default function DocReviewQueue({ onChanged }) {
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/pc/review-queue?limit=200")
    .then(({ data }) => { setData(data); setChecked(new Set()); })
    .catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    if (checked.size === 0) return toast.info("Select documents first");
    setBusy(true);
    try {
      const { data: r } = await api.post("/pc/review-bulk",
        { document_ids: [...checked], action });
      toast.success(`${r.updated} document(s) marked ${action.replace(/_/g, " ")}`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data || data.total === 0) return null;
  const rows = data.items || [];
  const allChecked = checked.size === rows.length && rows.length > 0;

  return (
    <div className="border border-amber-300 bg-amber-50/40 rounded-md" data-testid="doc-review-queue">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2.5">
        <ShieldCheck className="h-4 w-4 text-amber-700" />
        <span className="text-[12px] font-semibold text-amber-900">
          Review queue — {data.total} imported document(s) awaiting customer-safe review</span>
        {open ? <ChevronUp className="h-3.5 w-3.5 ml-auto text-amber-700" />
              : <ChevronDown className="h-3.5 w-3.5 ml-auto text-amber-700" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-slate-600 inline-flex items-center gap-1.5">
              <input type="checkbox" checked={allChecked} data-testid="rq-check-all"
                onChange={() => setChecked(allChecked ? new Set() : new Set(rows.map((r) => r.id)))} />
              Select all ({rows.length})</label>
            <span className="text-[11px] text-amber-800 font-medium">{checked.size} selected</span>
            <span className="ml-auto inline-flex gap-1.5">
              <button onClick={() => act("CUSTOMER_SAFE")} disabled={busy} data-testid="rq-customer-safe-btn"
                className="h-8 px-3 text-[11px] bg-emerald-700 text-white rounded inline-flex items-center gap-1 disabled:opacity-50">
                <ShieldCheck className="h-3 w-3" /> Mark Customer Safe</button>
              <button onClick={() => act("INTERNAL_ONLY")} disabled={busy} data-testid="rq-internal-btn"
                className="h-8 px-3 text-[11px] border border-slate-300 rounded inline-flex items-center gap-1 disabled:opacity-50 bg-white">
                <ShieldOff className="h-3 w-3" /> Keep Internal</button>
              <button onClick={() => act("RESTRICTED")} disabled={busy} data-testid="rq-restricted-btn"
                className="h-8 px-3 text-[11px] border border-rose-300 text-rose-700 rounded inline-flex items-center gap-1 disabled:opacity-50 bg-white">
                <Ban className="h-3 w-3" /> Restrict</button>
            </span>
          </div>
          <div className="max-h-64 overflow-auto space-y-1">
            {rows.map((d) => (
              <label key={d.id} className="flex items-center gap-2.5 bg-white border border-amber-100 rounded px-2.5 py-1.5 text-[11px] cursor-pointer"
                data-testid={`rq-row-${d.id}`}>
                <input type="checkbox" checked={checked.has(d.id)}
                  onChange={() => { const n = new Set(checked);
                    n.has(d.id) ? n.delete(d.id) : n.add(d.id); setChecked(n); }} />
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                  {TYPE_LABEL(d.doc_type)}</span>
                <span className="font-medium text-slate-800 truncate flex-1">{d.title}</span>
                <span className="text-slate-500 shrink-0">{d.brand || "—"} · {d.product_name?.slice(0, 30) || d.mpn || ""}</span>
                <span className="text-slate-400 font-mono shrink-0">{d.source || d.provider || ""}</span>
              </label>))}
          </div>
        </div>)}
    </div>
  );
}
